// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.EditReportButton.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React from 'react';
import PropTypes from 'prop-types';
import Button from 'share/components/controls/Izenda.Button';
import ReportHistory from 'modules/Reports/ReportList/ReportHistory/Izenda.ReportHistory';
import CopyReport from 'modules/Reports/ReportList/CopyReport/Izenda.CopyReport';
import connect from 'share/enhancers/Izenda.Connect';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import localizer from 'share/localization/Izenda.Localizer';
import { PERMISSION_KEY, ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import { EMPTY_GUID } from 'logic/data/Izenda.ReportsConstants';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import uiState from 'logic/Izenda.UiState';
import { SOURCE_PATH_QUERY_KEY } from 'logic/data/Izenda.RouteConstants';
import { isString, getOr, get } from 'lodash/fp';
import QueryStringUtil from 'share/utils/Izenda.QueryStringUtil';

const { FullAccess, QuickEdit, SaveAs } = ACCESS_RIGHT;

const buildSourcePath = (props: any) =>
  props.store.reportListLink || QueryStringUtil.getValueByKey(get('location.query', props), SOURCE_PATH_QUERY_KEY);

const EditReportButton = (props: any) => {
  const quickEdit = () => {
    const goToQuickEdit = () => {
      const { reportId } = props;
      const editUrl = routeUtils.getReportUrl('Edit', { id: reportId });
      const query = {};
      query[`${SOURCE_PATH_QUERY_KEY}`] = buildSourcePath(props);
      props.router.push({ pathname: editUrl, query });
    };
    const {
      fieldStore: { masterModel }
    } = props;
    const isDirty = masterModel.checkDirty();
    if (isDirty) {
      props
        .confirmFn('Confirmation', {
          description: localization.getText('REPORT_DESIGNER_MESSAGE_CONFIRM_QUICK_EDIT')
        })
        .then(() => {
          goToQuickEdit();
        });
    } else {
      goToQuickEdit();
    }
  };

  const designReport = () => {
    const {
      fieldStore: { masterModel },
      reportId
    } = props;

    const isDirty = masterModel.checkDirty();
    const query = {};
    query[`${SOURCE_PATH_QUERY_KEY}`] = buildSourcePath(props);
    const designUrl = routeUtils.getReportDesignerUrl('ReportDesignerDetail', { reportId: reportId });
    const goToReportDesigner = () => {props.router.push({ pathname: designUrl, query });};

    if(isDirty) {
      props.confirmFn('Confirmation', {
        description: localization.getText('REPORT_DESIGNER_MESSAGE_CONFIRM_EDIT')
      })
        .then(() => { goToReportDesigner(); })
    }
    else
    {
      goToReportDesigner();
    }
  };

  const showCopyPopup = () => {
    openCopyReportPopup(false);
  };

  const showMovePopup = () => {
    openCopyReportPopup(true);
  };

  const openCopyReportPopup = isMoveReport => {
    const {
      boardDetail,
      boardDetail: { version },
      store
    } = props;
    props
      .openPopup(CopyReport, {
        store,
        boardDetail,
        isMoveReport,
        version
      })
      .then((message, newReportId, newCateInfo) => {
        closeCopyPopup(message, newReportId, newCateInfo);
      });
  };

  const closeCopyPopup = (message, newReportId, newCateInfo) => {
    const { router, fieldStore, viewMode } = props;

    newReportId &&
      newCateInfo &&
      fieldStore.reportStore.updateReportCategory(newCateInfo.category, newCateInfo.subCategory);
    if (message) {
      return props
        .confirmFn('Information', {
          description: message,
          type: 'info',
          showCloseIndicator: true
        })
        .then(() => {
          if (isString(newReportId)) {
            !viewMode && router.push(buildViewerLink(newReportId));
          } else {
            router.push(props.location.pathname);
          }
        });
    }
  };

  const buildViewerLink = newReportId => routeUtils.getReportUrl('Edit', { id: newReportId });

  const showHistory = () => {
    const isGlobal = getOr(false, 'boardDetail.isGlobal', props);
    props.openPopup(ReportHistory, {
      showCopy: showCopyPopup,
      showMove: showMovePopup,
      reportId: props.reportId,
      masterStore: props.store,
      isGlobal: isGlobal,
      deleteHistory: deleteHistory
    });
  };

  const deleteHistory = () => {
    const { router, reportType, reportId } = props;

    if (get('state.fromList', props.location)) {
      router.goBack();
    } else {
      const reportInfo = getOr({}, 'masterModel', props.fieldStore);
      let categoryId = EMPTY_GUID;

      const assignCategoryId = catId => {
        String.isGuidFormat(catId) && catId !== EMPTY_GUID && (categoryId = catId);
      };

      assignCategoryId(reportInfo.categoryId);
      assignCategoryId(reportInfo.subCategoryId);

      var reportUrl = routeUtils.getReportUrl('List', {
        reportType: reportType,
        catId: categoryId,
        reportId: reportId
      });
      router.push(reportUrl);
    }
  };

  const { localization, authorization, accessPriority, active, boardDetail, viewMode, disabled } = props;

  const canOverwriteExistingReport = authorization.checkPrivileges([
    PERMISSION_KEY.ReportsActionsOverwriteExistingReport
  ]);

  const canCreateReport = authorization.checkPrivileges([PERMISSION_KEY.ReportsCanCreateNewReportValue]);

  const doesShowViewReportHistoryBtn = authorization.checkPrivileges([PERMISSION_KEY.ReportsActionsViewReportHistory]);

  const doesShowQuickEditBtn =
    (accessPriority === FullAccess || accessPriority === QuickEdit || accessPriority === SaveAs) && viewMode && active;
  const doesShowDesignBtn =
    (accessPriority === FullAccess || accessPriority === SaveAs) && active && canOverwriteExistingReport;
  const doesShowCopyBtn =
    (accessPriority === FullAccess || accessPriority === QuickEdit || accessPriority === SaveAs) && canCreateReport;
  const doesShowMoveBtn =
    (accessPriority === FullAccess && canOverwriteExistingReport && active && !boardDetail.isGlobal) ||
    (boardDetail.isGlobal && uiState.currentUser.systemAdmin && !uiState.tenantId);
  const hasReportDesignerLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.ReportDesigner);

  return (
    (doesShowQuickEditBtn ||
      doesShowDesignBtn ||
      doesShowCopyBtn ||
      doesShowMoveBtn ||
      doesShowViewReportHistoryBtn) && (
      <div className='btn-group'>
        <Button
          className='btn-report-default dropdown-toggle'
          data-toggle='dropdown'
          label={localization.getText('COMMON_LABEL_EDIT')}
          disabled={disabled}
        >
          <span className='caret' />
        </Button>
        <ul className='dropdown-menu'>
          {doesShowQuickEditBtn && (
            <li>
              <a onClick={quickEdit}>{localization.getText('REPORT_LABEL_QUICK_EDIT')}</a>
            </li>
          )}
          {hasReportDesignerLicense && doesShowDesignBtn && (
            <li>
              <a onClick={designReport}>{localization.getText('REPORT_LABEL_DESIGN')}</a>
            </li>
          )}
          {doesShowCopyBtn && (
            <li>
              <a onClick={showCopyPopup}>{localizer.getText('COMMON_LABEL_COPY')}</a>
            </li>
          )}
          {doesShowMoveBtn && (
            <li>
              <a onClick={showMovePopup}>{localizer.getText('COMMON_LABEL_MOVE')}</a>
            </li>
          )}
          {doesShowViewReportHistoryBtn && (
            <li>
              <a onClick={showHistory}>{localization.getText('REPORT_LABEL_VIEW_HISTORY')}</a>
            </li>
          )}
        </ul>
      </div>
    )
  );
};

EditReportButton.propTypes = {
  reportId: PropTypes.string.isRequired,
  viewMode: PropTypes.bool.isRequired,
  store: PropTypes.object.isRequired,
  confirmFn: PropTypes.func.isRequired,
  boardDetail: PropTypes.object
};

EditReportButton.contextTypes = {
  router: PropTypes.object
};

export default connect()(EditReportButton);



// WEBPACK FOOTER //
// ./ui/modules/Reports/Buttons/Izenda.EditReportButton.js